/**
 * Validates that a URL uses a safe HTTP(S) scheme.
 *
 * Parsing with the native `URL` constructor normalizes the input (case,
 * whitespace, control characters) before the protocol is checked, so spoofed
 * schemes such as `javascript:`, `JavaScript:`, `data:` or `vbscript:` are
 * reliably rejected. Protocol-relative (`//evil.com`) and relative URLs are
 * rejected as well, because they cannot be parsed as an absolute URL without a
 * base.
 *
 * Callers should fall back to rendering plain text when this returns
 * `undefined`, so an unsafe value can never become a clickable link.
 *
 * @param value - The raw URL string (e.g. a CMS-provided attribute value).
 * @returns The trimmed, validated `http(s)` URL, or `undefined` if unsafe.
 */
export function safeHttpUrl(value?: string): string | undefined {
    const trimmed = value?.trim();

    if (!trimmed) {
        return undefined;
    }

    // Root-relative paths are safe for same-origin navigation.
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
        return trimmed;
    }

    try {
        const { protocol } = new URL(trimmed);

        if (protocol === 'http:' || protocol === 'https:') {
            return trimmed;
        }
    } catch {
        // Not a valid absolute URL – treat as unsafe.
    }

    return undefined;
}
